import { useEffect } from 'react'
import { useAppStore } from '@shared/store'
import { Header } from '@widgets/header'
import { Footer } from '@widgets/footer'
import { useSeo } from '@shared/lib/seo'
import { SITE } from '@shared/config'
import * as s from './AboutPage.css'

const POLICIES = [
  {
    title: 'Who writes here',
    paras: [
      'Articles are written by that same circle of friends. Each writes inside the scope of their own experience. Hover any byline to see the contributor’s background.',
      'Where an article goes beyond what the author has personally grown or tested, the claim rests on the cited sources, not on first-hand observation. We do not use invented credentials or unsupported success rates.',
    ],
  },
  {
    title: 'Peer review before publication',
    paras: [
      'A second contributor reviews every article before it goes live, and is named on the article as Reviewed by. Review covers horticultural accuracy, safety boundaries, species fit, and whether limitations are stated honestly.',
      'A review is a professional read-through, not a laboratory replication. Where growers’ experiences differ, the article says so instead of presenting one experience as universal.',
    ],
  },
  {
    title: 'Sourcing standards',
    paras: [
      'Claims that affect plant health, safety, or product use are checked against the primary source for their scope: a product label, manufacturer documentation, extension publication, botanical reference, or peer-reviewed research.',
      'A source can support a narrow observation without proving the same outcome in every collection. That limitation stays visible in the article.',
    ],
  },
  {
    title: 'Use of AI tools',
    paras: [
      'Contributors sometimes use AI tools to polish wording, structure, and formatting. We do not rely on AI for the knowledge itself: the experience, facts, and recommendations come from the named contributor, and the peer reviewer checks the final text before publication.',
    ],
  },
  {
    title: 'How the site is funded',
    paras: [
      'Product recommendations start from what contributors have actually used in their own collections and setups. The site is funded through the Amazon Associates program: product links may earn a commission at no extra cost to the buyer, and those commissions cover hosting and operations. No manufacturer pays for placement.',
    ],
  },
  {
    title: 'Corrections',
    paras: [
      'We correct factual errors, broken links, outdated product details, and misleading wording. Each contributor stays responsible for their own articles and updates them when information turns out to be wrong or new findings change a recommendation. When a correction changes a recommendation, the article explains what changed.',
    ],
  },
]

export function AboutPage() {
  useSeo({
    title: `About | ${SITE.name}`,
    description: 'Who writes Caudexology, how articles are reviewed, and how the site is funded.',
    path: '/about/',
  })
  useEffect(() => { window.scrollTo(0, 0) }, [])
  const isDarkMode = useAppStore((st) => st.isDarkMode)

  return (
    <>
      <Header />
      <main className={s.page}>
        <section className={s.hero}>
          <div className={s.heroBrandRow}>
            <img src="/images/plantology_logo.png" alt="Plantology" className={s.heroLogo} style={isDarkMode ? { filter: 'invert(1)' } : undefined} />
            <span className={s.heroBrandDivider} aria-hidden="true" />
            <img src="/images/logo_black.webp" alt={SITE.name} className={s.heroSiteLogo} style={isDarkMode ? { filter: 'invert(1)' } : undefined} />
          </div>
          <h1 className={s.heroTitle}>Grow a thicker caudex, on purpose.</h1>
          <p className={s.heroDesc}>
            A field guide for caudiciform and succulent growers. Care guidance for the plants
            that store water in swollen stems, fat trunks, and underground caudices.
          </p>
        </section>

        <div className={s.shimmer} />

        <section className={s.section}>
          <p className={s.sectionLabel}>What we cover</p>
          <h2 className={s.sectionTitle}>Fat plants, in detail</h2>
          <p className={s.sectionBody}>
            We write about the specifics that decide whether a caudex fattens or rots:
            dormancy cycles, watering matched to the growth phase, mineral substrate ratios,
            light intensity, and grafting technique.
          </p>
          <div className={s.cardGrid}>
            <div className={s.card}>
              <span className={s.cardIcon}>🌵</span>
              <h3 className={s.cardTitle}>Caudiciforms</h3>
              <p className={s.cardText}>
                Pachypodium, Adenium, Dioscorea, Fockea, Stephania, and other caudex-forming
                species. Rooting, dormancy, and rot prevention.
              </p>
            </div>
            <div className={s.card}>
              <span className={s.cardIcon}>🪴</span>
              <h3 className={s.cardTitle}>Succulents &amp; Xerophytes</h3>
              <p className={s.cardText}>
                Grafting, seed-raising, and seasonal dormancy management for
                drought-adapted plants.
              </p>
            </div>
          </div>
        </section>

        <div className={s.shimmer} />

        <section className={`${s.section} ${s.storySection}`}>
          <img src="/images/plantology_logo.png" alt="" aria-hidden="true" className={s.storyLogo} />
          <p className={s.sectionLabel}>Where this started</p>
          <h2 className={s.sectionTitle}>A long argument between friends</h2>
          <p className={s.sectionBody}>
            This site grew out of a group of friends who share the hobby and bring
            different professional backgrounds to it. Our plants kept doing different
            things under supposedly identical care, so we argued about why, and settling
            each argument meant pulling sources and comparing notes. Somewhere in that
            pile of write-ups we decided a site like this ought to exist.
          </p>
          <p className={s.sectionBody}>
            As a joke we called the group PIT, our own plant-growers&apos; MIT. The name
            stuck, and PIT became Plantology Inst, the collective behind {SITE.name} and
            its sister sites.
          </p>
        </section>

        <div className={s.shimmer} />

        <section className={s.section}>
          <p className={s.sectionLabel}>Editorial policy</p>
          <h2 className={s.sectionTitle}>How this site works</h2>
          <div className={s.policyList}>
            {POLICIES.map((p, i) => (
              <div key={p.title} className={s.policyItem}>
                <span className={s.policyNum}>{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h3 className={s.policyTitle}>{p.title}</h3>
                  {p.paras.map((t) => (
                    <p key={t.slice(0, 24)} className={s.policyText}>{t}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={s.cta}>
          <h2 className={s.ctaTitle}>Contact</h2>
          <p className={s.ctaText}>
            Corrections, questions about a recommendation, and contributor inquiries.
          </p>
          <a className={s.ctaButton} href="mailto:plantology.inst@gmail.com">plantology.inst@gmail.com</a>
        </section>
      </main>
      <Footer />
    </>
  )
}
